import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { PageBar, Content } from '../components/Layout'
import { Badge, ErrorBox, KV, Loading, Panel } from '../components/ui'
import { api } from '../lib/api'
import { gbp, longDate, shortDate, timeOnly } from '../lib/format'
import type { AppSettings, Booking, Client, Invoice, Room, Service } from '../types'

const STATUSES = ['held', 'confirmed', 'done', 'cancelled'] as const

export function BookingDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['booking', id],
    queryFn: () => api.get<Booking>(`/bookings/${id}`),
  })
  const rooms = useQuery({ queryKey: ['rooms'], queryFn: () => api.get<Room[]>('/rooms') })
  const services = useQuery({ queryKey: ['services'], queryFn: () => api.get<Service[]>('/services') })
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.get<Client[]>('/clients') })
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api.get<AppSettings>('/settings') })

  const setStatus = useMutation({
    mutationFn: (status: string) => api.post(`/bookings/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking', id] })
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      queryClient.invalidateQueries({ queryKey: ['overview'] })
    },
  })

  const makeInvoice = useMutation({
    mutationFn: () => api.post<Invoice>(`/bookings/${id}/invoice`),
    onSuccess: (invoice) => navigate(`/invoices/${invoice.id}`),
  })

  if (isLoading) return <><PageBar title="Booking" /><Content><Loading /></Content></>
  if (error || !data) return <><PageBar title="Booking" /><Content><ErrorBox error={error} /></Content></>

  const client = clients.data?.find((c) => c.id === data.client_id)
  const room = rooms.data?.find((r) => r.id === data.room_id)
  const service = services.data?.find((s) => s.code === data.service_code)

  return (
    <>
      <PageBar
        title={`${client?.name ?? 'Booking'} — ${shortDate(data.starts_at)}`}
        actions={
          <>
            <Link className="btn ghost" to="/calendar">Calendar</Link>
            <button className="btn" type="button" disabled={makeInvoice.isPending} onClick={() => makeInvoice.mutate()}>
              {makeInvoice.isPending ? 'Creating…' : 'Create invoice'}
            </button>
          </>
        }
      />
      <Content>
        <div className="cols">
          <section>
            <Panel pad>
              <KV label="When">{longDate(data.starts_at)}, {timeOnly(data.starts_at)}–{timeOnly(data.ends_at)}</KV>
              <KV label="Duration">{data.hours.toFixed(1)} hours</KV>
              <KV label="Room">{room?.name ?? '—'}</KV>
              <KV label="Service">{service?.name ?? data.service_code}</KV>
              <KV label="Engineer">{data.engineer || '—'}</KV>
              <KV label="Client">
                {client ? <Link to={`/clients/${client.id}`}>{client.name}</Link> : '—'}
              </KV>
              {data.notes && <KV label="Notes">{data.notes}</KV>}
            </Panel>
          </section>
          <aside>
            <Panel pad>
              <div className="kv big"><span>Quoted</span><b>{gbp(data.quoted_pence)}</b></div>
              <KV label={`Deposit (${settings.data?.deposit_pct ?? 25}%)`}>{gbp(data.deposit_pence)}</KV>
              <KV label="Deposit paid">
                {data.deposit_paid_at ? shortDate(data.deposit_paid_at) : <span className="v-alert">no</span>}
              </KV>
              <KV label="Status"><Badge kind="b" value={data.status} /></KV>
              <div className="statusform">
                <select value={data.status} onChange={(event) => setStatus.mutate(event.target.value)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </Panel>
          </aside>
        </div>
      </Content>
    </>
  )
}
